import isEqual from 'lodash-es/isEqual';
import uniq from 'lodash-es/uniq';
import { call, takeLatest } from 'redux-saga/effects';

import type CarrierAccount from '~/types/CarrierAccount';

import { ActionKeys } from '~/actions/constants';
import { type createBatchTransactionsError } from '~/actions/orders-batch.actions';
import { countryHasMasterAccount } from '~/constants/countries';
import { getDefaultSenderAddress } from '~/selectors/address.selectors';
import { getUserCarrierAccounts } from '~/selectors/carrier-accounts.selectors';
import { getUserId } from '~/selectors/user.selectors';
import { type SavedAPIAddress } from '~/types/APIAddress';
import { Events, getLocalTraits, identify, isTraitDifferent, track } from '~/utils/segment';

import { selectState } from '..';

function* userCarrierAccountTraits() {
  const userId: null | number | undefined = yield selectState<null | number | undefined>(getUserId);
  const userCarrierAccounts: readonly CarrierAccount[] =
    yield selectState<readonly CarrierAccount[]>(getUserCarrierAccounts);
  if (userCarrierAccounts.length) {
    const localTraits = getLocalTraits();
    const personalCarrierNames = uniq(userCarrierAccounts.map((carrier) => carrier.carrier.name));
    if (
      (localTraits && !localTraits.personal_carriers) ||
      (localTraits &&
        localTraits.personal_carriers &&
        Array.isArray(localTraits.personal_carriers) &&
        !isEqual(personalCarrierNames, localTraits.personal_carriers))
    )
      yield call(identify, userId || undefined, { personal_carriers: personalCarrierNames });
  }
}

function* userAddressTraits() {
  const defaultSenderAddress: SavedAPIAddress | null = yield selectState<SavedAPIAddress | null>(
    getDefaultSenderAddress,
  );

  // NOTE(losch#ENG-21086|2022-01-14): Fix for legacy accounts whom didn't go through the onboarding flow.
  if (!defaultSenderAddress) return;

  const userId: null | number | undefined = yield selectState<null | number | undefined>(getUserId);
  if (
    defaultSenderAddress.country &&
    (isTraitDifferent('sender_address_city', defaultSenderAddress.city) ||
      isTraitDifferent('sender_address_phone', defaultSenderAddress.phone) ||
      isTraitDifferent('sender_address_state', defaultSenderAddress.state) ||
      isTraitDifferent('sender_address_country_name', defaultSenderAddress.country?.name) ||
      isTraitDifferent('email', defaultSenderAddress.email))
  ) {
    yield call(identify, userId || undefined, {
      master_account_available: countryHasMasterAccount(defaultSenderAddress.country),
      sender_address_city: defaultSenderAddress.city,
      sender_address_company: defaultSenderAddress.company,
      sender_address_country_name: defaultSenderAddress.country?.name,
      sender_address_email: defaultSenderAddress.email,
      sender_address_name: defaultSenderAddress.name,
      sender_address_phone: defaultSenderAddress.phone,
      sender_address_state: defaultSenderAddress.state,
    });
  }
}

function* ordersDownloadPackingSlip() {
  yield call(track, Events.packing_slip_downloaded);
}

function* sendLabelPurchaseFailedEvent(action: ReturnType<typeof createBatchTransactionsError>) {
  yield call(track, Events.label_purchase_failed, { error: action.payload.error });
}

export default [
  takeLatest(ActionKeys.ORDERS_GET_PACKING_SLIP, ordersDownloadPackingSlip),
  takeLatest(
    [ActionKeys.FETCH_CARRIER_ACCOUNTS_SUCCESS, ActionKeys.CREATE_CARRIER_ACCOUNT_SUCCESS],
    userCarrierAccountTraits,
  ),
  takeLatest(
    [
      ActionKeys.ADDRESSES_FETCH_SUCCESS,
      ActionKeys.APIADDRESS_CREATE,
      ActionKeys.APIADDRESS_EDIT,
      ActionKeys.APIADDRESS_DELETE,
    ],
    userAddressTraits,
  ),
  takeLatest(ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS_ERROR, sendLabelPurchaseFailedEvent),
];
